/**
 * Cancellation policies — `/money/cancellation-policies` (docs/05-admin/17 §2.5).
 *
 * **Built from the spec, not a design.** No design exists for this screen; the owner ruled on
 * 2026-10-26 that design-less screens are built from the docs with the console's own components
 * and restyled later. The doc names the route `/finance/…`; the console's money screens all live
 * under `/money/`, so it sits beside Refunds.
 *
 * A version is a grid: rows are hour windows before the ceremony, columns the five refund
 * components, cells the refundable fraction. Published versions are read-only because bookings pin
 * them; editing means "Start next version" (a draft copy), changing the draft's grid, and
 * publishing it. The tiling check here mirrors `tiling_problems()` in
 * `app/services/cancellation_policies.py` so the operator sees a gap before the server refuses
 * the publish — but the server's check is the one that counts.
 */
import { useState } from 'react';
import { Kicker, Note, StatusChip } from '../components/primitives';
import { DataTable, DetailPage } from '../components/data';
import { Input } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { RefundMatrixButton } from './RefundMatrix';
import {
  useCancellationPolicies,
  useCancellationPolicy,
  useDiscardPolicy,
  useDraftPolicy,
  usePublishPolicy,
  useReplacePolicyRules,
  type PolicyVersionDetail,
  type PolicyVersionRow,
  type PolicyWindow,
  type PolicyWindowIn,
} from '../lib/api/hooks/cancellationPolicies';

/** `RefundComponent`, in the order the published policy text lists them. */
export const COMPONENTS = [
  { key: 'service', label: 'Service' },
  { key: 'samagri', label: 'Samagri' },
  { key: 'travel', label: 'Travel' },
  { key: 'platform_fee', label: 'Platform fee' },
  { key: 'temple_charge', label: 'Temple charge' },
] as const;

type ComponentKey = (typeof COMPONENTS)[number]['key'];

/** One grid row as typed: strings, so a half-typed "0." is not coerced away mid-edit. */
export interface GridRow {
  min: string;
  max: string;
  cells: Record<ComponentKey, string>;
}

const emptyCells = (): Record<ComponentKey, string> =>
  Object.fromEntries(COMPONENTS.map((c) => [c.key, ''])) as Record<ComponentKey, string>;

/** The server's windows as grid rows, latest-before-start first (the doc's `≥72` row on top). */
export function toGrid(rules: readonly PolicyWindow[]): GridRow[] {
  const rows = new Map<string, GridRow>();
  for (const r of rules) {
    const id = `${r.hours_before_start_min}:${r.hours_before_start_max ?? ''}`;
    const row = rows.get(id) ?? {
      min: String(r.hours_before_start_min),
      max: r.hours_before_start_max === null || r.hours_before_start_max === undefined ? '' : String(r.hours_before_start_max),
      cells: emptyCells(),
    };
    row.cells[r.component as ComponentKey] = String(r.refundable_fraction);
    rows.set(id, row);
  }
  return [...rows.values()].sort((a, b) => Number(b.min) - Number(a.min));
}

/** Grid rows back to windows; an empty cell means "this component has no window here". */
export function toRules(rows: readonly GridRow[]): PolicyWindowIn[] {
  return rows.flatMap((row) =>
    COMPONENTS.filter((c) => row.cells[c.key].trim() !== '').map((c) => ({
      hours_before_start_min: Number(row.min),
      hours_before_start_max: row.max.trim() === '' ? null : Number(row.max),
      component: c.key,
      refundable_fraction: row.cells[c.key].trim(),
    })),
  );
}

const HOURS = /^\d{1,5}$/;
const FRACTION = /^(0(\.\d{1,4})?|1(\.0{1,4})?|\.\d{1,4})$/;

/** What would stop the server accepting the grid at all (422 on save). */
export function formatProblems(rows: readonly GridRow[]): string[] {
  const problems: string[] = [];
  rows.forEach((row, i) => {
    const n = i + 1;
    if (!HOURS.test(row.min.trim())) problems.push(`Row ${n}: "from" must be whole hours`);
    if (row.max.trim() !== '' && !HOURS.test(row.max.trim())) problems.push(`Row ${n}: "until" must be whole hours or blank`);
    for (const c of COMPONENTS) {
      const v = row.cells[c.key].trim();
      if (v !== '' && !FRACTION.test(v)) problems.push(`Row ${n}, ${c.label}: a fraction from 0 to 1 (0.5 is 50%), at most 4 places`);
    }
  });
  if (toRules(rows).length === 0) problems.push('A version needs at least one window');
  return problems;
}

/** A port of `tiling_problems()`: every component must answer every hour from 0 upward, once. */
export function tilingProblems(rules: readonly PolicyWindowIn[]): string[] {
  const problems: string[] = [];
  for (const { key } of COMPONENTS) {
    const mine = rules.filter((w) => w.component === key).sort((a, b) => a.hours_before_start_min - b.hours_before_start_min);
    if (mine.length === 0) {
      problems.push(`${key}: no windows`);
      continue;
    }
    let expected = 0;
    for (const w of mine) {
      if (w.hours_before_start_min > expected) problems.push(`${key}: gap from ${expected} h to ${w.hours_before_start_min} h`);
      else if (w.hours_before_start_min < expected) problems.push(`${key}: overlap at ${w.hours_before_start_min} h`);
      if (w.hours_before_start_max === null || w.hours_before_start_max === undefined) {
        expected = -1;
        break;
      }
      if (w.hours_before_start_max <= w.hours_before_start_min) {
        problems.push(`${key}: window ${w.hours_before_start_min}–${w.hours_before_start_max} h is empty`);
      }
      expected = w.hours_before_start_max;
    }
    if (expected !== -1) problems.push(`${key}: nothing covers ${expected} h and beyond`);
    else if (mine[mine.length - 1]!.hours_before_start_max != null) problems.push(`${key}: a window follows the open-ended one`);
  }
  return problems;
}

export function windowLabel(row: GridRow): string {
  return row.max.trim() === '' ? `${row.min} h or more` : `${row.min}–<${row.max} h`;
}

const STATUS_TONE: Record<string, 'success' | 'warn' | 'neutral'> = { active: 'success', draft: 'warn', retired: 'neutral' };

export function statusChip(status: string) {
  return <StatusChip tone={STATUS_TONE[status] ?? 'neutral'} label={status} />;
}

const cellStyle = { padding: 'var(--sp-xs) var(--sp-sm)', borderBottom: '1px solid var(--c-border)', textAlign: 'left' as const };

function ReadOnlyMatrix({ rows }: { rows: readonly GridRow[] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-admin-ui)' }}>
      <thead>
        <tr>
          <th style={cellStyle}>Cancelled</th>
          {COMPONENTS.map((c) => (
            <th key={c.key} style={cellStyle}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.min}:${row.max}`}>
            <td style={cellStyle}>{windowLabel(row)} before</td>
            {COMPONENTS.map((c) => (
              <td key={c.key} className="tabular" style={cellStyle}>
                {row.cells[c.key] === '' ? '—' : `${Math.round(Number(row.cells[c.key]) * 1000) / 10}%`}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DraftEditor({ policy, onDiscarded }: { policy: PolicyVersionDetail; onDiscarded: () => void }) {
  const { scope, toast } = useConsole();
  const replace = useReplacePolicyRules();
  const publish = usePublishPolicy();
  const discard = useDiscardPolicy();
  const [baseline, setBaseline] = useState(policy.rules);
  const [rows, setRows] = useState<GridRow[]>(() => toGrid(policy.rules));
  const [confirm, setConfirm] = useState<'publish' | 'discard' | null>(null);
  if (baseline !== policy.rules) {
    setBaseline(policy.rules);
    setRows(toGrid(policy.rules));
  }

  const dirty = JSON.stringify(rows) !== JSON.stringify(toGrid(policy.rules));
  const format = formatProblems(rows);
  const tiling = format.length === 0 ? tilingProblems(toRules(rows)) : [];
  const editable = scope === 'superadmin' || scope === 'finance';
  const update = (i: number, patch: Partial<GridRow>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const fail = (e: unknown) => toast({ tone: 'error', message: errorMessage(e) });

  return (
    <>
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        {rows.map((row, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 'var(--sp-sm)',
              alignItems: 'end',
              flexWrap: 'wrap',
              padding: 'var(--sp-sm)',
              border: '1px solid var(--c-border)',
              borderRadius: 'var(--r-tile)',
            }}
          >
            <Input density="admin" label={`Row ${i + 1} from (h)`} value={row.min} disabled={!editable} onChange={(v) => update(i, { min: v })} style={{ width: 96 }} />
            <Input
              density="admin"
              label={`Row ${i + 1} until (h)`}
              value={row.max}
              placeholder="no limit"
              disabled={!editable}
              onChange={(v) => update(i, { max: v })}
              style={{ width: 96 }}
            />
            {COMPONENTS.map((c) => (
              <Input
                key={c.key}
                density="admin"
                label={`Row ${i + 1} ${c.label}`}
                value={row.cells[c.key]}
                placeholder="0–1"
                inputMode="decimal"
                disabled={!editable}
                onChange={(v) => update(i, { cells: { ...row.cells, [c.key]: v } })}
                style={{ width: 104 }}
              />
            ))}
            <ScopeAction scope={scope} need="finance" size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove row
            </ScopeAction>
          </div>
        ))}
      </div>

      {format.length > 0 && (
        <ul aria-label="Fix before saving" style={{ color: 'var(--c-error-text)', margin: 0 }}>
          {format.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {tiling.length > 0 && (
        <ul aria-label="Fix before publishing" style={{ color: 'var(--c-warn-text)', margin: 0 }}>
          {tiling.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          variant="secondary"
          onClick={() => setRows([...rows, { min: '', max: '', cells: emptyCells() }])}
        >
          Add window
        </ScopeAction>
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          disabled={!dirty || format.length > 0 || replace.isPending}
          onClick={() =>
            replace.mutate(
              { id: policy.id, rules: toRules(rows) },
              { onSuccess: () => toast({ tone: 'success', message: 'Draft windows saved' }), onError: fail },
            )
          }
        >
          Save windows
        </ScopeAction>
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          variant="primary"
          disabled={dirty || tiling.length > 0 || format.length > 0}
          onClick={() => setConfirm('publish')}
        >
          Publish v{policy.version}
        </ScopeAction>
        <ScopeAction scope={scope} need="finance" size="sm" variant="ghost" onClick={() => setConfirm('discard')}>
          Discard draft
        </ScopeAction>
        {dirty && <span style={{ color: 'var(--c-warn-text)', fontSize: 'var(--fs-admin-colhead)' }}>Unsaved — save the windows before publishing.</span>}
      </div>

      <ConfirmAction
        open={confirm === 'publish'}
        title={`Publish ${policy.name} v${policy.version}`}
        phrase="PUBLISH"
        reason
        confirmLabel="Publish"
        consequences={[
          `Applies to bookings created from now; existing bookings keep the version they pinned${policy.version > 1 ? ` (v${policy.version - 1} or earlier)` : ''}.`,
          'The version it replaces is retired, never deleted.',
          'The devotee-facing policy text is CMS copy and is updated there, separately.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) =>
          publish.mutate(
            { id: policy.id, reason },
            {
              onSuccess: () => {
                setConfirm(null);
                toast({ tone: 'success', message: `${policy.name} v${policy.version} published` });
              },
              onError: fail,
            },
          )
        }
      />
      <ConfirmAction
        open={confirm === 'discard'}
        title={`Discard draft v${policy.version}`}
        phrase="DISCARD"
        tone="warn"
        confirmLabel="Discard draft"
        consequences={['The draft and its windows are thrown away. Nothing published changes.']}
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          discard.mutate(
            { id: policy.id },
            {
              onSuccess: () => {
                setConfirm(null);
                toast({ tone: 'success', message: `Draft v${policy.version} discarded` });
                // Back to the list: staying on a version that no longer exists refetched it into
                // "Could not read this version" (found live).
                onDiscarded();
              },
              onError: fail,
            },
          )
        }
      />
    </>
  );
}

function PolicyDetail({ id, onClose, onOpen }: { id: number; onClose: () => void; onOpen: (id: number) => void }) {
  const { scope, toast } = useConsole();
  const query = useCancellationPolicy(id);
  const draft = useDraftPolicy();
  const p = query.data;

  return (
    <DetailPage
      title={p ? `${p.name} v${p.version}` : 'Cancellation policy'}
      status={p && statusChip(p.status)}
      onClose={onClose}
      actions={
        p &&
        p.status !== 'draft' && (
          <ScopeAction
            scope={scope}
            need="finance"
            size="sm"
            variant="secondary"
            disabled={draft.isPending}
            onClick={() =>
              draft.mutate(
                { id: p.id },
                {
                  onSuccess: (next) => {
                    toast({ tone: 'success', message: `Draft v${next!.version} started from v${p.version}` });
                    onOpen(next!.id);
                  },
                  onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                },
              )
            }
          >
            Start next version
          </ScopeAction>
        )
      }
    >
      {query.isPending && <LoadingState message="Reading the version…" />}
      {query.isError && <ErrorState title="Could not read this version" cause={errorMessage(query.error)} />}
      {p && (
        <Section
          title={p.status === 'draft' ? 'Windows — draft' : 'Windows'}
          note={`${p.pinned_bookings} ${p.pinned_bookings === 1 ? 'booking pins' : 'bookings pin'} this version`}
        >
          <Kicker>
            {p.status === 'draft'
              ? 'Each cell is the share of that component refunded when the devotee cancels inside the window. Blank means no window for that component in that row.'
              : 'Published versions are read-only: bookings pin them. Start the next version to change the policy.'}
          </Kicker>
          {p.status === 'draft' ? <DraftEditor policy={p} onDiscarded={onClose} /> : <ReadOnlyMatrix rows={toGrid(p.rules)} />}
        </Section>
      )}
    </DetailPage>
  );
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function CancellationPolicies() {
  const query = useCancellationPolicies();
  const [open, setOpen] = useState<number | null>(null);
  const rows: readonly PolicyVersionRow[] = query.data ?? [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <RefundMatrixButton />
      </div>
      <Note>
        Refunds are computed from the version a booking pinned at checkout, never from whatever is
        current. Publishing a new version changes future bookings only.
      </Note>
      {query.isPending && <LoadingState message="Reading the policies…" />}
      {query.isError && <ErrorState title="Could not read the policies" cause={errorMessage(query.error)} />}
      {query.isSuccess && rows.length === 0 && (
        <EmptyState icon="scale" title="No cancellation policy yet" body="The launch policy is seeded; an empty list means the seed has not run here." />
      )}
      {rows.length > 0 && (
        <Section title="Versions" note="Newest version first" pad={false}>
          <DataTable
            density="compact"
            columns={[
              { key: 'name', label: 'Policy' },
              { key: 'version', label: 'Version', mono: true },
              { key: 'status', label: 'Status' },
              { key: 'effective', label: 'Effective from' },
              { key: 'rules', label: 'Windows', align: 'right' },
              { key: 'pinned', label: 'Bookings pinned', align: 'right' },
            ]}
            rows={rows.map((r) => ({
              id: r.id,
              name: r.name,
              version: `v${r.version}`,
              status: statusChip(r.status),
              effective: fmtDate(r.effective_from),
              rules: r.rule_count,
              pinned: r.pinned_bookings,
            }))}
            onRowClick={(row) => setOpen(Number(row.id))}
          />
        </Section>
      )}
      {open !== null && <PolicyDetail id={open} onClose={() => setOpen(null)} onOpen={setOpen} />}
    </>
  );
}
